import React, { useRef, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { Formik, FormikProps } from 'formik';
import { useMutation } from '@apollo/client';

import { useRouter } from 'next/router';
import { handleValidationErrors, onCompletedHandler, onErrorHandler } from '@/services/apollo';

import Title from '@/components/commons/page-elements/Title';
import { UPDATE_USER } from '@/components/settings/Actions';
import Button, { ButtonSize, ButtonType } from '@/components/commons/button';
import routes from '@/services/routes';
import Page from '@/components/layout/page';
import ActionLoader from '@/components/commons/action_loader';
import WarningCard from '@/components/commons/warning_card';
import { withAuth } from '@/components/with_auth';
import {
    UserContent, initializedAtom, userAtom,
    showFolderOnboarding,
    onboardingAtom,
} from '@/components/with_auth/State';
import { haveErrors, reportGeneralError } from '@/components/onboarding/Shared';
import ThumbsUpOutline from '@/components/commons/icons/ThumbsUpOutline';

import OnboardingFoldersModal from '@/components/inbox/monitored-labels/OnboardingFolderModal';
import MonitoredLabelsTable from '@/components/inbox/monitored-labels/MonitoredLabelTable';
import styles from '@/components/inbox/monitored-labels/MonitoredLabels.module.css';

interface Props {
    // user gets injected from withAuth HOC
    readonly user: UserContent
}

function MonitoredLabel({ user }: Props) {
    const router = useRouter();
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const isFolderOnboarding = useRecoilValue(showFolderOnboarding);
    const onboardingData = useRecoilValue(onboardingAtom);
    const folderStatusformRef = useRef<FormikProps<any>>(null);

    const setters = {
        setUser: useSetRecoilState(userAtom),
        setInitialized: useSetRecoilState(initializedAtom),
    };

    const handleFolderStatus = () => {
        if (folderStatusformRef.current) {
            folderStatusformRef.current.handleSubmit();
        }
    };

    const [updateUser] = useMutation(UPDATE_USER);

    const handleSubmit = async (_frm, { setStatus, setSubmitting }) => {
        const attributes = {
            hasCreatedFolders: true,
        };

        await updateUser({
            variables: {
                updateUserInput: {
                    attributes,
                },
            },
            notifyOnNetworkStatusChange: true,
            fetchPolicy: 'no-cache',
            onError: onErrorHandler((error) => {
                setSubmitting(false);
                reportGeneralError(error);
            }),
            onCompleted: onCompletedHandler(() => { }, (data) => {
                setSubmitting(false);
                const { errors } = data.updateUser;
                if (haveErrors(errors)) {
                    handleValidationErrors(errors, setStatus, 'user');
                    return;
                }

                setters.setUser(data.updateUser.user);
                setters.setInitialized(true);
                setStatus({ errors: {} });

                router.push(routes.MY_SIXTY);
            }),
        });
    };

    return (
        <Page>
            <div className={`flex flex-wrap fullHeightLessNavPage bg-lightGray bg-opacity-30 items-start ${styles.home}`}>
                <div className="w-full p-4 sm:p-8">
                    <div className="flex justify-between relative flex-col md:flex-row">
                        <Title
                            title="Smart Folders"
                            externalStyles="mb-8"
                        >
                            <div className="flex flex-wrap gap-2 justify-between items-center w-full">
                                <p>Smart Folders assist you in managing your inbox every day.</p>
                                {isFolderOnboarding && (
                                    <Formik
                                        innerRef={folderStatusformRef}
                                        onSubmit={handleSubmit}
                                        initialValues={{
                                            hasCreatedFolders: user?.hasCreatedFolders,
                                        }}
                                    >
                                        <Button
                                            size={ButtonSize.base}
                                            icon={<ThumbsUpOutline className="h-4 w-4 mr-3" />}
                                            text="I'm done"
                                            type={ButtonType.submit}
                                            onClick={handleFolderStatus}
                                        />
                                    </Formik>
                                )}
                            </div>
                        </Title>
                        <ActionLoader isLoading={loading} />
                    </div>
                    {
                        formError !== '' && (
                            <div className="w-full my-[20px]">
                                <WarningCard message={formError} />
                            </div>
                        )
                    }
                    <div className="bg-cirrusWhite rounded-md shadow-md">
                        <MonitoredLabelsTable
                            setLoading={setLoading}
                            formError={formError}
                            setFormError={setFormError}
                        />
                    </div>
                </div>
            </div>
            {isFolderOnboarding && onboardingData.folders === 1 && <OnboardingFoldersModal />}
        </Page>
    );
}

export default withAuth(MonitoredLabel);
